import { parseNetlifyResponseBody, postToNetlifyFunction, readNetlifyErrorMessage } from './netlifyClient';

export type NotificationEventType = 'recipe_created' | 'comment_created' | 'recipe_realization_created';
interface NotificationDispatchResponse {
  mode?: 'outbox' | 'legacy-direct';
  recipientsCount?: number;
  successCount?: number;
  failedCount?: number;
}

export interface NotificationEventPayload {
  eventType: NotificationEventType;
  recipeId?: string;
  commentId?: number;
  realizationId?: number;
  recipeTitle?: string;
  commentPreview?: string;
  realizationPreview?: string;
}

class NotificationService {
  private getDeliveryCounts(body: NotificationDispatchResponse | null): {
    recipientsCount: number;
    successCount: number;
    failedCount: number;
  } {
    return {
      recipientsCount: body?.recipientsCount ?? 0,
      successCount: body?.successCount ?? 0,
      failedCount: body?.failedCount ?? 0,
    };
  }

  private async dispatchNotification(payload: NotificationEventPayload): Promise<Response | null> {
    return postToNetlifyFunction({
      functionName: 'notifications',
      payload,
      missingTokenStrategy: 'skip',
    });
  }

  private async readDispatchResponse(
    response: Response
  ): Promise<NotificationDispatchResponse | null> {
    return (await parseNetlifyResponseBody(response)) as NotificationDispatchResponse | null;
  }

  private shouldWarnForEmptyDelivery(body: NotificationDispatchResponse | null): boolean {
    return body?.mode !== 'outbox' && this.getDeliveryCounts(body).successCount === 0;
  }

  private logEmptyDeliveryWarning(body: NotificationDispatchResponse | null): void {
    const { recipientsCount, failedCount } = this.getDeliveryCounts(body);
    console.warn('Notification dispatched with no successful delivery:', {
      recipientsCount,
      failedCount,
    });
  }

  private async handleFailedDispatch(response: Response): Promise<void> {
    const detail = await readNetlifyErrorMessage(
      response,
      `Notification dispatch failed (${response.status})`
    );
    console.warn('Notification dispatch failed:', response.status, detail);
  }

  public async notify(payload: NotificationEventPayload): Promise<void> {
    try {
      const response = await this.dispatchNotification(payload);

      if (!response) {
        return;
      }

      if (!response.ok) {
        await this.handleFailedDispatch(response);
        return;
      }

      const body = await this.readDispatchResponse(response);

      if (this.shouldWarnForEmptyDelivery(body)) {
        this.logEmptyDeliveryWarning(body);
      }
    } catch (error) {
      console.warn('Notification dispatch error:', error);
    }
  }

  public async notifyRecipeCreated(recipeId: string, recipeTitle?: string): Promise<void> {
    return this.notify({
      eventType: 'recipe_created',
      recipeId,
      recipeTitle,
    });
  }

  public async notifyCommentCreated(
    recipeId: string,
    commentPreview?: string,
    commentId?: number
  ): Promise<void> {
    return this.notify({
      eventType: 'comment_created',
      recipeId,
      commentId,
      commentPreview,
    });
  }

  public async notifyRecipeRealizationCreated(
    recipeId: string,
    realizationPreview?: string,
    realizationId?: number
  ): Promise<void> {
    return this.notify({
      eventType: 'recipe_realization_created',
      recipeId,
      realizationId,
      realizationPreview,
    });
  }
}

export const notificationService = new NotificationService();
